import React from 'react';
import { motion } from 'framer-motion';
import { 
  ChatBubbleLeftRightIcon, 
  SignalSlashIcon, 
  CpuChipIcon,
  ShieldCheckIcon 
} from '@heroicons/react/24/outline';

const AiApp = () => {
  const features = [
    {
      name: 'ChatGPT Alternative',
      description: 'Powerful conversational AI that understands context, generates creative content, and assists with complex tasks - all without internet.',
      icon: ChatBubbleLeftRightIcon,
    },
    {
      name: '100% Offline',
      description: 'No internet connection required. Your data stays private and secure on your device, perfect for sensitive work and remote locations.',
      icon: SignalSlashIcon,
    },
    {
      name: 'Local Processing',
      description: 'Advanced AI models optimized to run efficiently on your hardware. Fast responses without cloud dependencies or subscription fees.',
      icon: CpuChipIcon,
    },
    {
      name: 'Privacy First',
      description: 'Your conversations never leave your device. Complete data privacy and security for personal and professional use.',
      icon: ShieldCheckIcon,
    },
  ];

  return (
    <div id="ai-app" className="bg-gradient-to-br from-dark-800 to-dark-900 py-24 sm:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary-500">AI Assistant</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Your Offline AI Companion
            </p>
            <p className="mt-6 text-lg leading-8 text-primary-200">
              Experience the power of advanced AI without the internet. Our models rival ChatGPT and Gemini 
              while keeping your data completely private and accessible anywhere, anytime.
            </p>
          </motion.div>
        </div>
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none relative z-10">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
            {features.map((feature, index) => (
              <motion.div 
                key={feature.name} 
                className="flex flex-col"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <motion.dt 
                  className="flex items-center gap-x-3 text-base font-semibold leading-7 text-white"
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div 
                    className="p-2 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <feature.icon className="h-6 w-6 flex-none text-white" aria-hidden="true" />
                  </motion.div>
                  {feature.name}
                </motion.dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-primary-200">
                  <p className="flex-auto">{feature.description}</p>
                </dd>
              </motion.div>
            ))}
          </dl>
        </div>
        <div className="mt-20 text-center relative z-10">
          <motion.div 
            className="rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 p-8 text-white shadow-2xl border border-primary-500/20"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-4">Ready to Experience Offline AI?</h3>
            <p className="text-primary-100 mb-6">
              Join thousands of users who have made the switch to private, offline AI assistance.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <motion.a
                href="https://play.google.com/store/apps/details?id=com.speedrai.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-black rounded-lg hover:bg-gray-800 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zM16.8 15.099l2.302 2.302a1 1 0 00.448-.833V7.432a1 1 0 00-.448-.833l-2.302 2.302 2.3 2.298zM5.864 21.342l10.937 6.333-8.635-8.635-2.302 2.302z"/>
                </svg>
                <span className="text-white font-medium">Get it on Google Play</span>
              </motion.a>
              
              <motion.a
                href="https://apps.apple.com/us/app/speedr-ai-run-without-internet/id6743132806"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-black rounded-lg hover:bg-gray-800 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span className="text-white font-medium">Download on App Store</span>
              </motion.a>
            </div>
            
            <motion.button 
              className="rounded-md bg-dark-900 px-8 py-3 text-base font-semibold text-primary-400 shadow-sm hover:bg-dark-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 border border-primary-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Learn More About AI Chat
            </motion.button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AiApp;
